/**
 * The public acquisition funnel's event taxonomy.
 *
 * Everything the marketing site reports goes through this module. Two rules
 * hold it together:
 *
 * 1. **Closed vocabularies.** Every property value comes from a literal union
 *    declared here, and `ctaClickPayload` re-checks membership at runtime. No
 *    caller can widen the vocabulary by passing a string through, so no
 *    repository name, file path, email address, scan result, or query string
 *    can reach the analytics provider — there is no field for one to occupy.
 * 2. **One call per interaction.** Events fire from user event handlers only,
 *    never from effects or render, so a re-render, a StrictMode double-invoke,
 *    or a client-side navigation cannot re-fire an event that already fired.
 *
 * The taxonomy is documented for non-engineers in
 * `docs/analytics-event-taxonomy.md`; keep the two in step. The server repeats
 * these allow-lists in `backend/internal/analytics/public.go`.
 */
import { PUBLIC_PAGES } from '@/lib/seo/public-routes';

import { reportAnalytics } from './transport';

/** A public page was shown. */
export const PAGEVIEW_EVENT = 'pageview';

/** A visitor acted on a call to action. */
export const CTA_CLICK_EVENT = 'cta_click';

/** A visitor followed a link in the site chrome that is not a call to action. */
export const NAVIGATION_CLICK_EVENT = 'navigation_click';

/**
 * What the visitor is being asked to do. Named for the destination's job, not
 * its URL, so a route change does not silently split a funnel's history.
 */
export const CTA_ACTIONS = [
  'signup',
  'waitlist',
  'login',
  'cli_docs',
  'product_docs',
  'contact_sales',
  'sample_report',
] as const;
export type CtaAction = (typeof CTA_ACTIONS)[number];

/**
 * Where on the page the control sits. This is the discriminator that makes two
 * clicks on the same action comparable: a hero signup and a footer signup are
 * the same `action` with different `placement`.
 */
export const CTA_PLACEMENTS = [
  'header_nav',
  'mobile_nav',
  'hero',
  'after_pr',
  'pricing_card',
  'final_cta',
  'guide_cta',
  'footer',
] as const;
export type CtaPlacement = (typeof CTA_PLACEMENTS)[number];

/**
 * The control's rank in the page's CTA hierarchy, as decided in
 * `docs/landing-page-positioning.md`. Filled controls are primary; everything
 * that offers an alternative path — the CLI, sales, logging in — is secondary.
 */
export const CTA_PRIORITIES = ['primary', 'secondary'] as const;
export type CtaPriority = (typeof CTA_PRIORITIES)[number];

/**
 * Which public page the click happened on, as a registry key rather than a
 * URL. Anything not in the public registry reports `other`, so an authenticated
 * path — which can carry a repository or organisation identifier — never
 * becomes a property value.
 */
export const CTA_SURFACES = [...(Object.keys(PUBLIC_PAGES) as (keyof typeof PUBLIC_PAGES)[]), 'other'] as const;
export type CtaSurface = (typeof CTA_SURFACES)[number];

export interface CtaClick {
  action: CtaAction;
  placement: CtaPlacement;
  priority: CtaPriority;
  surface: CtaSurface;
}

const SURFACE_BY_PATH: ReadonlyMap<string, CtaSurface> = new Map(
  Object.entries(PUBLIC_PAGES).map(([key, route]) => [route.path, key as CtaSurface]),
);

/**
 * Resolves a location pathname to a surface.
 *
 * Only the pathname is consulted — never the search string or hash, which are
 * visitor-supplied and can carry arbitrary content.
 */
export function surfaceForPath(pathname: string): CtaSurface {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return SURFACE_BY_PATH.get(normalized || '/') ?? 'other';
}

function member<T extends string>(allowed: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

/**
 * Validates a click against the taxonomy and returns the payload to send.
 *
 * Returns `null` for anything that does not match, so a bad call drops the
 * event instead of forwarding an unvetted value. TypeScript already rejects
 * these at build time; the runtime check covers untyped call sites and keeps
 * the guarantee testable.
 */
export function ctaClickPayload(input: CtaClick): Record<CtaProperty, string> | null {
  if (!member(CTA_ACTIONS, input.action)) return null;
  if (!member(CTA_PLACEMENTS, input.placement)) return null;
  if (!member(CTA_PRIORITIES, input.priority)) return null;
  if (!member(CTA_SURFACES, input.surface)) return null;

  return {
    action: input.action,
    placement: input.placement,
    priority: input.priority,
    surface: input.surface,
  };
}

/** The complete set of property keys a `cta_click` carries. */
export const CTA_PROPERTIES = ['action', 'placement', 'priority', 'surface'] as const;
export type CtaProperty = (typeof CTA_PROPERTIES)[number];

/**
 * Reports one CTA click.
 *
 * Call this from a click handler. It never throws: analytics failing is not a
 * reason for a signup button to stop working.
 */
function reportablePathname(): string | null {
  if (typeof window === 'undefined') return null;
  const cleaned = reportableUrl(window.location.href);
  if (!cleaned) return null;
  try {
    return new URL(cleaned).pathname;
  } catch {
    return null;
  }
}

export function trackCtaClick(input: CtaClick): void {
  const payload = ctaClickPayload(input);
  if (!payload) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] dropped a cta_click outside the taxonomy', input);
    }
    return;
  }
  const path = reportablePathname();
  if (!path) return;

  try {
    reportAnalytics({ name: CTA_CLICK_EVENT, path, properties: payload });
  } catch {
    // Reporting is best effort.
  }
}

/* ------------------------------------------------------------------ *
 * What the provider is allowed to see at all
 * ------------------------------------------------------------------ */

/**
 * Public paths that carry no identifier but are not in the page registry,
 * because they are never indexed. They are the funnel's end points, so their
 * page views are what make a conversion rate computable at all.
 */
const AUTH_ENTRY_PATHS: readonly string[] = ['/login', '/signup', '/register', '/waitlist'];

/**
 * Blog posts are public content addressed by a human-written slug. The pattern
 * mirrors the one the route registry enforces, so a path that could not have
 * been generated by the registry is not reported either.
 */
const BLOG_POST_PATH = /^\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*$/;

function normalizePathname(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return trimmed || '/';
}

/**
 * Whether a pathname may be reported to the analytics provider.
 *
 * This is an allow-list, and deliberately fails closed. Most of the
 * application is not on it: `/repositories/:id`, `/issues/:id`,
 * `/reports/:id`, `/share/reports/:token`, `/admin/*`, `/reset-password` and
 * `/verify-email` all carry an identifier, a token, or a secret in the URL,
 * and a page view would hand that identifier to a third party. Measuring the
 * signed-in product is not this provider's job.
 */
export function isReportablePathname(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (SURFACE_BY_PATH.has(normalized)) return true;
  if (AUTH_ENTRY_PATHS.includes(normalized)) return true;
  return BLOG_POST_PATH.test(normalized);
}

/**
 * Reduces an event URL to the part that is safe to report, or `null` to drop
 * the event.
 *
 * The search string and the fragment are always discarded, whatever the path:
 * both are visitor-supplied, both have carried a token on this site
 * (`/reset-password?token=`), and neither is worth the risk. That costs UTM
 * attribution, which is the intended trade.
 */
export function reportableUrl(rawUrl: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    // An unparseable URL cannot be vetted, so it is not reported.
    return null;
  }

  if (!isReportablePathname(parsed.pathname)) return null;
  return `${parsed.origin}${normalizePathname(parsed.pathname)}`;
}

interface PageViewEvent {
  type: 'pageview';
  url: string;
}

interface FilteredCustomEvent {
  type: 'event';
  url: string;
}

export type BeforeSendEvent = PageViewEvent | FilteredCustomEvent;

/**
 * Drops a page view whose URL is outside the public allow-list, and strips the
 * query string and fragment from one that stays.
 *
 * Click events apply the same rule through `reportablePathname` before they
 * are sent, so a click on a signed-in or tokenized route is dropped as well.
 */
export function analyticsBeforeSend(event: BeforeSendEvent): BeforeSendEvent | null {
  const url = reportableUrl(event.url);
  return url ? { ...event, url } : null;
}

/* ------------------------------------------------------------------ *
 * Navigation
 * ------------------------------------------------------------------ */

/**
 * Where a chrome navigation link goes, as a name rather than a URL.
 *
 * Only the site chrome — header, mobile menu, footer — reports navigation.
 * Links inside prose are left alone: they are part of the reading experience,
 * not a measured path through the site.
 */
export const NAV_DESTINATIONS = [
  'home',
  'features',
  'pricing',
  'product_docs',
  'roadmap',
  'blog',
  'changelog',
  'technical_debt_scanner',
  'ci_cd_quality_gates',
  'legal_privacy',
  'legal_terms',
  'legal_refund',
  'dashboard',
  'sample_report',
] as const;
export type NavDestination = (typeof NAV_DESTINATIONS)[number];

export const NAV_PLACEMENTS = ['header_nav', 'mobile_nav', 'footer'] as const;
export type NavPlacement = (typeof NAV_PLACEMENTS)[number];

const NAV_DESTINATION_BY_PATH: ReadonlyMap<string, NavDestination> = new Map([
  ['/', 'home'],
  ['/#features', 'features'],
  ['/pricing', 'pricing'],
  ['/docs', 'product_docs'],
  ['/roadmap', 'roadmap'],
  ['/blog', 'blog'],
  ['/changelog', 'changelog'],
  ['/technical-debt-scanner', 'technical_debt_scanner'],
  ['/ci-cd-quality-gates', 'ci_cd_quality_gates'],
  ['/legal/privacy', 'legal_privacy'],
  ['/legal/terms', 'legal_terms'],
  ['/legal/refund', 'legal_refund'],
  ['/dashboard', 'dashboard'],
  ['/sample-report', 'sample_report'],
]);

/**
 * Resolves a link target to a destination name, or `null` when the target is
 * not a chrome navigation link. Resolving rather than accepting a name means a
 * new link in the chrome is instrumented by adding its path here, in one
 * place, instead of at every call site.
 */
export function navDestinationFor(to: string): NavDestination | null {
  return NAV_DESTINATION_BY_PATH.get(to) ?? null;
}

export interface NavigationClick {
  destination: NavDestination;
  placement: NavPlacement;
  surface: CtaSurface;
}

export const NAV_PROPERTIES = ['destination', 'placement', 'surface'] as const;
export type NavProperty = (typeof NAV_PROPERTIES)[number];

export function navigationClickPayload(input: NavigationClick): Record<NavProperty, string> | null {
  if (!member(NAV_DESTINATIONS, input.destination)) return null;
  if (!member(NAV_PLACEMENTS, input.placement)) return null;
  if (!member(CTA_SURFACES, input.surface)) return null;

  return {
    destination: input.destination,
    placement: input.placement,
    surface: input.surface,
  };
}

/** Reports one navigation click. Call from a click handler, never an effect. */
export function trackNavigationClick(input: NavigationClick): void {
  const payload = navigationClickPayload(input);
  if (!payload) {
    if (import.meta.env.DEV) {
      console.warn('[analytics] dropped a navigation_click outside the taxonomy', input);
    }
    return;
  }

  const path = reportablePathname();
  if (!path) return;

  try {
    reportAnalytics({ name: NAVIGATION_CLICK_EVENT, path, properties: payload });
  } catch {
    // Reporting is best effort.
  }
}
